import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getRouteMeta, getRouteSchema, getCanonicalUrl, normalizeRoutePath, NOINDEX_ROUTES, ROUTE_META } from '../seo-config.js';

// Upsert a <meta>/<link> head tag identified by an attribute selector.
function setHeadTag(selector, createTag, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(createTag);
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  return el;
}

// Keeps document.title and the meta[name=description] tag in sync with
// the current route on client-side navigation. The initial HTML already
// has the correct per-route title/description baked in by
// scripts/prerender.mjs — this just keeps things consistent after the
// user navigates via React Router (no full page reload).
//
// products-data.js (~44KB) is only dynamically imported when the route
// actually is a product page, so it stays out of the initial bundle.
export default function RouteMeta() {
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;

    async function applyMeta() {
      // The server 301s every page to its trailing-slash URL, so
      // location.pathname is e.g. "/about/" — normalise it back to the
      // route key ("/about") before ROUTE_META/NOINDEX_ROUTES lookups or
      // every page would be flagged as unknown and marked noindex.
      const pathname = normalizeRoutePath(location.pathname);
      const productMatch = pathname.match(/^\/aircons\/([^/]+)\/?$/);
      const products = productMatch
        ? (await import('./aircons/products-data.js')).PRODUCTS
        : undefined;

      if (cancelled) return;

      // Unknown URL -> the catch-all NotFoundPage. Flag noindex so stray
      // URLs that reach the SPA fallback aren't indexed as homepage clones,
      // instead of inheriting the homepage title/description by default.
      const productMissing = productMatch && !products?.some((p) => p.slug === productMatch[1]);
      const isUnknown = (!ROUTE_META[pathname] && !productMatch) || productMissing;

      const meta = isUnknown
        ? { title: 'Page Not Found | Aircons Cape Town', description: "This page doesn't exist." }
        : getRouteMeta(pathname, products);
      if (!meta) return;

      document.title = meta.title;
      const descTag = document.querySelector('meta[name="description"]');
      if (descTag) descTag.setAttribute('content', meta.description);

      // Keep canonical + social URL/tags in sync with the prerendered head.
      const canonical = getCanonicalUrl(pathname);
      setHeadTag('link[rel="canonical"]', 'link', { rel: 'canonical', href: canonical });
      setHeadTag('meta[property="og:title"]', 'meta', { property: 'og:title', content: meta.title });
      setHeadTag('meta[property="og:description"]', 'meta', { property: 'og:description', content: meta.description });
      setHeadTag('meta[property="og:url"]', 'meta', { property: 'og:url', content: canonical });
      setHeadTag('meta[name="twitter:title"]', 'meta', { name: 'twitter:title', content: meta.title });
      setHeadTag('meta[name="twitter:description"]', 'meta', { name: 'twitter:description', content: meta.description });
      setHeadTag('meta[name="twitter:url"]', 'meta', { name: 'twitter:url', content: canonical });

      // noindex applies only to routes flagged in seo-config.js (thank-you);
      // remove it everywhere else.
      const robotsTag = document.querySelector('meta[name="robots"]');
      if (isUnknown || NOINDEX_ROUTES.has(pathname)) {
        setHeadTag('meta[name="robots"]', 'meta', { name: 'robots', content: 'noindex, follow' });
      } else if (robotsTag) {
        robotsTag.remove();
      }

      const schema = isUnknown ? [] : getRouteSchema(pathname, products);
      const existingLd = document.querySelector('script[type="application/ld+json"]');
      if (isUnknown && existingLd) {
        existingLd.remove();
      }
      if (schema.length) {
        let ldTag = document.querySelector('script[type="application/ld+json"]');
        if (!ldTag) {
          ldTag = document.createElement('script');
          ldTag.type = 'application/ld+json';
          document.head.appendChild(ldTag);
        }
        ldTag.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': schema,
        });
      }
    }

    applyMeta();
    return () => { cancelled = true; };
  }, [location.pathname]);

  return null;
}
